/**
 * TenantPicker — selector de tenant reusable con clasificación SCRUM-294
 * (cliente / demo con seed / demo por lead) visible en cada opción.
 * Extraído para SCRUM-303: AdminEmailGatesPanel.tsx tenía su propio <select>
 * suelto sin esta clasificación — tercera ubicación del mismo problema.
 *
 * SCRUM-318/326/329: upgrade a combobox con búsqueda (Popover+Command,
 * mismo patrón que el selector de listas de precio) — con decenas de
 * tenants/demos un <select> plano es ingobernable. Mismo picker canónico,
 * no se crea un catálogo paralelo — cada caller sigue trayendo su propia
 * lista de tenants como antes.
 */
import { useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { getWorkspaceKind, WORKSPACE_KIND_LABELS } from "@/lib/workspace-kind";
import { cn } from "@/lib/utils";

export type TenantPickerOption = {
  id: string;
  name: string;
  slug: string;
  productFlags?: Record<string, boolean>;
};

export function TenantPicker({
  tenants,
  value,
  onChange,
  placeholder = "Elegir tenant",
  className,
}: {
  tenants: TenantPickerOption[];
  value: string;
  onChange: (id: string) => void;
  placeholder?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => tenants.find((t) => t.id === value), [tenants, value]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className={cn("justify-between font-normal", className ?? "w-[280px]")}>
          <span className="truncate">
            {selected ? `${selected.name} (${selected.slug})` : placeholder}
          </span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar tenant…" className="h-9" />
          <CommandList>
            <CommandEmpty>Sin resultados</CommandEmpty>
            <CommandGroup>
              {tenants.map((t) => {
                const kind = getWorkspaceKind(t.productFlags);
                return (
                  <CommandItem
                    key={t.id}
                    value={`${t.name} ${t.slug}`}
                    onSelect={() => {
                      onChange(t.id);
                      setOpen(false);
                    }}
                    className="gap-1.5"
                  >
                    <Check className={cn("h-3.5 w-3.5 shrink-0", value === t.id ? "opacity-100" : "opacity-0")} />
                    <span className="truncate">
                      {t.name} ({t.slug})
                      {kind !== "client" ? ` · ${WORKSPACE_KIND_LABELS[kind]}` : ""}
                    </span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
